import React, { type ComponentProps } from 'react';
import styled from '@emotion/styled';
import {
  backgroundColor,
  borderColor,
  justifyContent,
  position,
  space,
  themeGet,
  zIndex,
} from 'styled-system';
import type {
  BackgroundColorProps,
  BorderColorProps,
  JustifyContentProps,
  PositionProps,
  ZIndexProps,
} from 'styled-system';

import { Button } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { GO_TO_TOP_Z_INDEX } from 'common/constants/z-index';
import { useScrollToTop } from 'common/hooks/scroll';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import {
  flexShrink,
  themeBottom,
  themeBoxShadow,
  themeHeight,
  themeRight,
  themeWidth,
} from 'common/theme/system-utilities';
import type {
  FlexShrinkProps,
  ThemeBottomProps,
  ThemeRightProps,
} from 'common/theme/system-utilities';
import { useBottomMargin } from '../hooks/use-bottom-margin';

const ICON_SIZE = 32;
const BUTTON_SIZE = 40;

const StyledGoToTopButton = styled(Button)<
  PositionProps &
    ZIndexProps &
    SpaceProps &
    ThemeRightProps &
    ThemeBottomProps &
    BackgroundColorProps &
    BorderColorProps &
    JustifyContentProps
>`
  ${backgroundColor};
  ${borderColor};
  ${themeBoxShadow};
  ${position};
  ${space};
  ${themeHeight};
  ${themeWidth};
  ${themeBottom};
  ${themeRight};
  ${zIndex};
  ${justifyContent};

  &:hover > svg {
    fill: ${themeGet(`colors.sys.neutral.icon.hover`)};
  }

  &:active > svg {
    fill: ${themeGet(`colors.sys.neutral.icon.active`)};
  }
`;

const goToTopButtonStyles: ComponentProps<typeof StyledGoToTopButton> = {
  kind: 'secondary-v2',
  position: 'fixed',
  zIndex: GO_TO_TOP_Z_INDEX,
  p: 'sp_8',
  themeWidth: BUTTON_SIZE,
  themeHeight: BUTTON_SIZE,
  themeRight: ['sp_16', 'sp_24', 'sp_48', 'sp_64'],
  backgroundColor: 'sys.neutral.background.default',
  borderColor: 'sys.neutral.border.inverted',
  isRounded: true,
  boxShadow: 3,
  justifyContent: 'center',
};

const UnStyledIcon = styled(Icon)<FlexShrinkProps>`
  ${flexShrink};
`;

const iconStyles: ComponentProps<typeof UnStyledIcon> = {
  name: 'arrowLightDown',
  rotate: 'rotate(180deg)',
  fill: 'sys.neutral.border.strong',
  flexShrink: 0,
  size: ICON_SIZE,
};

const GoToTopButton = () => {
  const { t } = useLocalization();
  const { scrollToTop, isButtonVisible } = useScrollToTop();
  const bottomMargin = useBottomMargin();

  if (!isButtonVisible) {
    return null;
  }

  return (
    <StyledGoToTopButton
      {...goToTopButtonStyles}
      data-testid="go-to-top-button"
      data-corgi-id="go-to-top-button"
      onClick={scrollToTop}
      themeBottom={bottomMargin}
    >
      <UnStyledIcon {...iconStyles} ariaLabel={t('h24_scroll_to_top_img_alt')} />
    </StyledGoToTopButton>
  );
};

export default GoToTopButton;
